import {
  format,
  isToday,
  isTomorrow,
  isYesterday,
  differenceInMinutes,
  differenceInHours,
  differenceInSeconds,
  parseISO,
} from 'date-fns'
import i18n from '@/i18n'

function resolveDate(input: Date | string): Date {
  if (typeof input === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return parseISO(input + 'T00:00:00')
    return parseISO(input)
  }
  return input
}

/**
 * DD.MM.YYYY — the canonical eKool UI date format.
 * Drops year when it matches the current year if `shortYear` is true.
 */
export function formatDate(input: Date | string, shortYear = false): string {
  const d = resolveDate(input)
  if (shortYear && d.getFullYear() === new Date().getFullYear()) {
    return format(d, 'dd.MM')
  }
  return format(d, 'dd.MM.yyyy')
}

/** 24-hour time: HH:mm */
export function formatTime(input: Date | string): string {
  return format(resolveDate(input), 'HH:mm')
}

/** DD.MM.YYYY HH:mm — full datetime stamp */
export function formatDateTime(input: Date | string): string {
  const d = resolveDate(input)
  return `${formatDate(d)} ${formatTime(d)}`
}

/** Short label for schedule headings: "Mon, 31 Mar" */
export function formatWeekdayDate(input: Date | string): string {
  const d = resolveDate(input)
  return format(d, 'EEE, d MMM')
}

/** Long label for agenda/calendar: "Monday, 31 March" */
export function formatLongDate(input: Date | string): string {
  const d = resolveDate(input)
  return format(d, 'EEEE, d MMMM')
}

/** Short date without year: "31 Mar" */
export function formatShortDate(input: Date | string): string {
  return format(resolveDate(input), 'd MMM')
}

/** Month + year header: "March 2026" */
export function formatMonthYear(input: Date | string): string {
  return format(resolveDate(input), 'MMMM yyyy')
}

/** Short weekday: "Mon" */
export function formatWeekday(input: Date | string): string {
  return format(resolveDate(input), 'EEE')
}

/**
 * Semantic date label following eKool guidelines:
 * - "Today", "Tomorrow", "Yesterday" for ±1 day
 * - Weekday name (e.g. "Monday") within ~7 days
 * - DD.MM.YYYY beyond that
 *
 * Optionally appends the short date for context (e.g. "Today, 31 Mar").
 */
export function formatSemanticDate(input: Date | string, withDate = true): string {
  const d = resolveDate(input)
  const t = i18n.t

  let label: string | null = null
  if (isToday(d)) label = t('common.today')
  else if (isTomorrow(d)) label = t('common.tomorrow')
  else if (isYesterday(d)) label = t('common.yesterday')

  if (label) {
    return withDate ? `${label}, ${formatShortDate(d)}` : label
  }

  const now = new Date()
  const diffDays = Math.abs(Math.floor((d.getTime() - now.getTime()) / 86_400_000))
  if (diffDays <= 7) {
    const weekday = format(d, 'EEEE')
    return withDate ? `${weekday}, ${formatShortDate(d)}` : weekday
  }

  return formatDate(d)
}

/**
 * Relative time for recent activity:
 * - < 10s → "Just now"
 * - < 60s → "30s ago"
 * - < 60m → "5 min ago"
 * - < 24h → "3h ago"
 * - older  → DD.MM.YYYY
 */
export function formatTimeAgo(date: Date | null): string {
  if (!date) return ''
  const now = new Date()
  const t = i18n.t

  const diffSec = differenceInSeconds(now, date)
  if (diffSec < 10) return t('common.justNow')
  if (diffSec < 60) return t('common.timeAgoSeconds', { count: diffSec })

  const diffMin = differenceInMinutes(now, date)
  if (diffMin < 60) return t('common.timeAgoMinutes', { count: diffMin })

  const diffHour = differenceInHours(now, date)
  if (diffHour < 24) return t('common.timeAgoHours', { count: diffHour })

  return formatDate(date)
}

/** YYYY-MM-DD for internal/data use (form values, keys, mock data). */
export function toISODateString(input: Date | string): string {
  return format(resolveDate(input), 'yyyy-MM-dd')
}

/** Time range display: "09:00 – 10:30" */
export function formatTimeRange(start: string, end: string): string {
  return `${start}\u2009–\u2009${end}`
}

/** Date range display: "31.03 – 02.04.2026" or "31.03.2026" if same day */
export function formatDateRange(from: Date | string, to: Date | string): string {
  const f = resolveDate(from)
  const t = resolveDate(to)
  if (toISODateString(f) === toISODateString(t)) return formatDate(f)
  return `${formatDate(f)}\u2009–\u2009${formatDate(t)}`
}
